import React, { useState } from 'react';
import ImageUpload from '../ImageUpload';
import { addArtPiece, updateArtPiece, deleteArtPiece } from '../../../services/dataService';

const ArtAdmin = ({ siteData, updateSiteData, setSiteData, showSaveStatus }) => {
    const [saving, setSaving] = useState(false);
    const [editingPieces, setEditingPieces] = useState({});

    const handleAddArtPiece = async () => {
        try {
            setSaving(true);
            const newPiece = {
                title: 'New Art Piece',
                description: '',
                image: '',
                imagePath: '',
                medium: '',
                dimensions: '',
                year: new Date().getFullYear().toString(),
                available: true
            };

            const pieceId = await addArtPiece(newPiece);
            const pieceWithId = { ...newPiece, id: pieceId };

            // Update local state directly
            setSiteData(prev => ({
                ...prev,
                art: {
                    ...prev.art,
                    pieces: [...(prev.art.pieces || []), pieceWithId]
                }
            }));

            showSaveStatus('Art piece added successfully!');
        } catch (error) {
            console.error('Failed to add art piece:', error);
            showSaveStatus('Failed to add art piece', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteArtPiece = async (piece) => {
        if (window.confirm('Are you sure you want to delete this art piece?')) {
            try {
                setSaving(true);
                await deleteArtPiece(piece.id, piece.imagePath);

                // Update local state directly
                const updatedPieces = siteData.art.pieces.filter(p => p.id !== piece.id);
                setSiteData(prev => ({
                    ...prev,
                    art: {
                        ...prev.art,
                        pieces: updatedPieces
                    }
                }));

                showSaveStatus('Art piece deleted successfully!');
            } catch (error) {
                console.error('Failed to delete art piece:', error);
                showSaveStatus('Failed to delete art piece', true);
            } finally {
                setSaving(false);
            }
        }
    };

    const startEditing = (pieceId) => {
        const piece = siteData.art.pieces.find(p => p.id === pieceId);
        setEditingPieces(prev => ({
            ...prev,
            [pieceId]: { ...piece }
        }));
    };

    const cancelEditing = (pieceId) => {
        setEditingPieces(prev => {
            const newEditing = { ...prev };
            delete newEditing[pieceId];
            return newEditing;
        });
    };

    const handleFieldChange = (pieceId, field, value) => {
        setEditingPieces(prev => ({
            ...prev,
            [pieceId]: {
                ...prev[pieceId],
                [field]: value
            }
        }));
    };

    const savePiece = async (pieceId) => {
        try {
            setSaving(true);
            const updatedPiece = editingPieces[pieceId];

            await updateArtPiece(pieceId, updatedPiece);

            // Update local state directly
            const updatedPieces = siteData.art.pieces.map(p =>
                p.id === pieceId ? updatedPiece : p
            );

            setSiteData(prev => ({
                ...prev,
                art: {
                    ...prev.art,
                    pieces: updatedPieces
                }
            }));

            // Clear from editing
            setEditingPieces(prev => {
                const newEditing = { ...prev };
                delete newEditing[pieceId];
                return newEditing;
            });

            showSaveStatus('Art piece updated successfully!');
        } catch (error) {
            console.error('Failed to update art piece:', error);
            showSaveStatus('Failed to update art piece', true);
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (pieceId, field, pathField, imageUrl, imagePath) => {
        try {
            const pieceToUpdate = siteData.art.pieces.find(p => p.id === pieceId);
            const updatedPiece = {
                ...pieceToUpdate,
                [field]: imageUrl,
                [pathField]: imagePath
            };

            await updateArtPiece(pieceId, updatedPiece);

            // Update local state directly
            const updatedPieces = siteData.art.pieces.map(p =>
                p.id === pieceId ? updatedPiece : p
            );

            setSiteData(prev => ({
                ...prev,
                art: {
                    ...prev.art,
                    pieces: updatedPieces
                }
            }));

            showSaveStatus('Art piece image updated successfully!');
        } catch (error) {
            console.error('Failed to update art piece image:', error);
            showSaveStatus('Failed to update art piece image', true);
        }
    };

    const updateArtData = async (updates) => {
        try {
            const updatedArt = {
                ...siteData.art,
                ...updates
            };

            setSiteData(prev => ({
                ...prev,
                art: updatedArt
            }));

            await updateSiteData('art', updatedArt);
        } catch (error) {
            console.error('Failed to update art:', error);
            showSaveStatus('Failed to update art section', true);
        }
    };

    const isEditing = (pieceId) => !!editingPieces[pieceId];
    const getPieceData = (pieceId) => {
        return editingPieces[pieceId] || siteData.art.pieces.find(p => p.id === pieceId);
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Art Portfolio</h2>
                <button
                    onClick={handleAddArtPiece}
                    disabled={saving}
                    className="bg-sage-600 text-white px-4 py-2 rounded-lg hover:bg-sage-700 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Art Piece'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <input
                    type="text"
                    value={siteData.art.title || ''}
                    onChange={(e) => updateArtData({ title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                    placeholder="Art Commissions"
                />
            </div>

            <div className="space-y-6">
                {siteData.art.pieces?.map((piece) => {
                    const pieceData = getPieceData(piece.id);
                    const editing = isEditing(piece.id);

                    return (
                        <div key={piece.id} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Piece: {piece.title}</h3>
                                <div className="flex space-x-2">
                                    {editing ? (
                                        <>
                                            <button
                                                onClick={() => savePiece(piece.id)}
                                                disabled={saving}
                                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                                            >
                                                {saving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                onClick={() => cancelEditing(piece.id)}
                                                disabled={saving}
                                                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => startEditing(piece.id)}
                                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                        >
                                            Edit
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteArtPiece(piece)}
                                        disabled={saving}
                                        className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                        <input
                                            type="text"
                                            value={pieceData.title}
                                            onChange={(e) => editing ? handleFieldChange(piece.id, 'title', e.target.value) : null}
                                            disabled={!editing}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            value={pieceData.description}
                                            onChange={(e) => editing ? handleFieldChange(piece.id, 'description', e.target.value) : null}
                                            disabled={!editing}
                                            rows={3}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Medium</label>
                                            <input
                                                type="text"
                                                value={pieceData.medium}
                                                onChange={(e) => editing ? handleFieldChange(piece.id, 'medium', e.target.value) : null}
                                                disabled={!editing}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                                placeholder="Oil on canvas, Digital, etc."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Dimensions</label>
                                            <input
                                                type="text"
                                                value={pieceData.dimensions}
                                                onChange={(e) => editing ? handleFieldChange(piece.id, 'dimensions', e.target.value) : null}
                                                disabled={!editing}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                                placeholder="24x36 inches"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                            <input
                                                type="text"
                                                value={pieceData.year}
                                                onChange={(e) => editing ? handleFieldChange(piece.id, 'year', e.target.value) : null}
                                                disabled={!editing}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Available for Commission</label>
                                            <select
                                                value={pieceData.available}
                                                onChange={(e) => editing ? handleFieldChange(piece.id, 'available', e.target.value === 'true') : null}
                                                disabled={!editing}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                            >
                                                <option value="true">Yes</option>
                                                <option value="false">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Artwork Image</label>
                                    <ImageUpload
                                        currentImage={piece.image}
                                        currentImagePath={piece.imagePath}
                                        onImageUpload={(imageUrl, imagePath) =>
                                            handleImageUpload(piece.id, 'image', 'imagePath', imageUrl, imagePath)
                                        }
                                        label="Upload Artwork"
                                        folder={`art/${piece.id}`}
                                        aspectRatio={3 / 4}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}

                {(!siteData.art.pieces || siteData.art.pieces.length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No art pieces added yet. Click "Add Art Piece" to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArtAdmin;